import { HttpParams } from '@angular/common/http';
import { NgZone } from '@angular/core';
import { DEFAULT_PAGE_SIZE } from '../app.constants';

declare var $: any;

export class DatatablesUtils {
  // `dom` strings for the bounded-scroll data grid (see `_data-card.scss`
  // `.bk-data-card--grid`): only the table (`rt`) sits in the
  // `.bk-data-card__scroll` div so the controls don't scroll sideways with it.
  // The `.dt-head` wrapper holds the length menu (`l`) and search box (`f`) above
  // the table; the `.dt-foot` holds the entries-count info (`i`) and, when paged,
  // the pagination (`p`) below it. Non-paged tables keep the info line but have no
  // length menu (DataTables suppresses it when `paging:false`) or pager. (`f`/`i`
  // are no-ops when a table sets `searching:false` / `info:false`.)
  static readonly BOUNDED_SCROLL_DOM = '<"dt-head"lf><"bk-data-card__scroll"rt><"dt-foot"ip>';
  static readonly BOUNDED_SCROLL_DOM_NO_PAGING = '<"dt-head"f><"bk-data-card__scroll"rt><"dt-foot"i>';

  static buildDatatablesSettings(extra: any): DataTables.Settings {
    // Show 4 page-number buttons at the start/end of the range instead of the
    // default 5 (numbers_length 7 → 5, 6 → 4; the middle window stays 3). Keeps the
    // pager compact, which matters most on the bounded-scroll card at phone width.
    // numbers_length is a global DataTables setting; set it once, guarded so it is
    // a no-op if DataTables/jQuery isn't loaded yet.
    if (typeof $ !== 'undefined' && $.fn?.dataTable?.ext?.pager) {
      $.fn.dataTable.ext.pager.numbers_length = 6;
    }
    const { initComplete, ajax, ...rest } = extra || {};
    const dtOptions: DataTables.Settings = {
      // 'scrollY': '490px',
      pagingType: 'full_numbers',
      pageLength: DEFAULT_PAGE_SIZE,
      scrollCollapse: true,
      // dom: '<"top"flp<"clear">>',
      ...rest,
      initComplete: function (this: any, settings: any, json: any) {
        DatatablesUtils.attachSearchTrim(this);
        if (typeof initComplete === 'function') {
          initComplete.call(this, settings, json);
        }
      },
    };
    if (typeof ajax === 'function') {
      dtOptions.ajax = (data: any, callback: any, settings: any) => {
        if (data?.search && typeof data.search.value === 'string') {
          data.search.value = data.search.value.trim();
        }
        return ajax(data, callback, settings);
      };
    } else if (ajax !== undefined) {
      dtOptions.ajax = ajax;
    }
    return dtOptions;
  }

  // DataTables builds its own filter input (not an Angular template), so trim
  // leading/trailing spaces when the user submits with Enter or leaves the field.
  static attachSearchTrim(table: any): void {
    const api = typeof table?.api === 'function' ? table.api() : table;
    const container = api?.table?.()?.container?.();
    if (!container) return;
    const input = $(container).find('input[type="search"], .dataTables_filter input').get(0) as HTMLInputElement | undefined;
    if (!input || (input as any)._bkTrimSearch) return;
    (input as any)._bkTrimSearch = true;
    const trim = () => {
      const trimmed = String(input.value || '').trim();
      if (input.value === trimmed) return;
      input.value = trimmed;
      if (typeof api.search === 'function') {
        api.search(trimmed);
        if (typeof api.draw === 'function') api.draw();
      }
    };
    input.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Enter') trim();
    });
    input.addEventListener('blur', trim);
  }

  // Mirror bkScrollShadow's horizontal edge fade on the DataTables-generated
  // `.bk-data-card__scroll` element. The directive can't attach to a runtime-
  // built element, so wire it from the table's draw callbacks instead. Call
  // `attachScrollShadow` from `drawCallback`/`initComplete`, and
  // `refreshScrollShadow` from a `@HostListener('window:resize')`.
  static attachScrollShadow(host: HTMLElement, zone: NgZone): void {
    zone.runOutsideAngular(() => {
      const el = host?.querySelector('.bk-data-card__scroll') as HTMLElement | null;
      if (!el) { return; }
      // Wire the scroll listener once per element; DataTables may rebuild the
      // element (re-init), so a fresh element re-wires via the missing flag.
      if (!(el as any)._bkScrollShadow) {
        (el as any)._bkScrollShadow = true;
        el.addEventListener('scroll', () => DatatablesUtils.refreshScrollShadow(host), { passive: true });
      }
      DatatablesUtils.refreshScrollShadow(host);
    });
  }

  static refreshScrollShadow(host: HTMLElement): void {
    const el = host?.querySelector('.bk-data-card__scroll') as HTMLElement | null;
    if (!el) { return; }
    const max = el.scrollWidth - el.clientWidth;
    const overflowing = max > 1;
    el.classList.toggle('bk-scroll--more-left', overflowing && el.scrollLeft > 1);
    el.classList.toggle('bk-scroll--more-right', overflowing && el.scrollLeft < max - 1);
  }

  static convertToApiParams(dtParams: any): any {
    const params: any = {};
    if (dtParams) {
      params.offset = dtParams.start;
      params.limit = dtParams.length;
      params.search = typeof dtParams.search?.value === 'string'
        ? dtParams.search.value.trim()
        : dtParams.search?.value;
      if (dtParams.order && dtParams.order.length) {
        params.sortField = dtParams.order.map((o) => dtParams.columns[o.column]?.data).join(',');
        params.sortDir = dtParams.order.map((o) => o.dir).join(',');
      }
    }
    return params;
  }
}
